import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { OneWebComponentsAngularModule } from '@one/angular';
import { QnSbnService } from '../../services/qn-sbn-service.service';

interface FeedbackQuestionAnswer {
  FeedbackType?: string;
  FeedbackQuestion?: string;
  FeedbackAnswer?: string;
  MandatoryOrOptional?: string;
}

interface FeedbackDetailsRow {
  AccountId: string;
  AccountName: string;
  Status: string;
  IsLsoAcknowledged: boolean | string;
  CustomerAttachment: string;
  Submitter: string;
  QuestionsAndAnswers: FeedbackQuestionAnswer[];
}

@Component({
  selector: 'dl-feedback-details-audit-trail',
  standalone: true,
  imports: [CommonModule, OneWebComponentsAngularModule],
  templateUrl: './feedback-details-audit-trail.component.html',
  styleUrl: './feedback-details-audit-trail.component.scss'
})
export class FeedbackDetailsAuditTrailComponent implements OnChanges {
  private readonly changeDetect = inject(ChangeDetectorRef);
  private readonly qnSbnService = inject(QnSbnService);

  @Input() inputData: any;
  @Input() notificationId = '';
  @Output() closed = new EventEmitter<void>();

  public displayedColumns = [
    'AccountId',
    'AccountName',
    'Status',
    'IsLsoAcknowledged',
    'CustomerAttachment',
    'Submitter',
    'QuestionsAndAnswers'
  ];
  public dataSource: FeedbackDetailsRow[] = [];
  public pagedData: FeedbackDetailsRow[] = [];
  public pageSize = 10;
  public currentPage = 0;
  public pageSizeOptions = [10, 25, 50, 100, 250, 500];
  public isPageSizeMenuOpen = false;
  public expandedElement: FeedbackDetailsRow | null = null;
  public isLoading = false;
  public errorMessage = '';

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['notificationId'] && this.notificationId) {
      this.loadFeedbackDetails(this.notificationId);
    } else if (changes['inputData']) {
      this.dataSource = this.toRows(changes['inputData'].currentValue);
      this.setupPagination();
    }
  }



  public get totalCount(): number {
    return this.dataSource.length;
  }

  public setupPagination(): void {
    this.currentPage = 0;
    this.updatePaginatedData();
  }

  public expandToggle(element: FeedbackDetailsRow): void {
    this.expandedElement = this.expandedElement === element ? null : element;
    this.changeDetect.detectChanges();
  }

  public hasFeedbackDetails(element: FeedbackDetailsRow): boolean {
    return element.QuestionsAndAnswers.length > 0;
  }

  public close(): void {
    this.closed.emit();
  }

  private loadFeedbackDetails(notificationId: string): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.expandedElement = null;

    this.qnSbnService.getAccountsFeedbackResponses(notificationId).subscribe({
      next: response => {
        this.dataSource = this.toRows(response);
        this.setupPagination();
        this.isLoading = false;
        this.changeDetect.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
        this.dataSource = [];
        this.setupPagination();
        this.isLoading = false;
        this.errorMessage = this.getErrorMessage(error);
        this.changeDetect.detectChanges();
      }
    });
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    const responseMessage = typeof error.error === 'string'
      ? error.error
      : error.error?.message || error.error?.body;

    return responseMessage
      ? `Unable to load feedback details (${error.status}): ${responseMessage}`
      : `Unable to load feedback details (${error.status}): ${error.statusText || 'Request failed'}`;
  }

  private toRows(inputData: any): FeedbackDetailsRow[] {
    const accounts = Array.isArray(inputData) ? inputData : inputData?.Accounts;

    if (!Array.isArray(accounts)) {
      return [];
    }

    return accounts.map((account: any) => {
      const contacts = Array.isArray(account.Contacts) ? account.Contacts : [];
      const comments = contacts
        .map((contact: any) => contact.CustomerAttachment?.Comments)
        .filter((comment: any) => comment !== undefined && comment !== null && comment !== '')
        .map((comment: any) => String(comment))
        .join('\n');
      const contactSubmitters = contacts
        .map((contact: any) => contact.Submitter)
        .filter((submitter: any) => submitter !== undefined && submitter !== null && submitter !== '');

      return {
        AccountId: this.display(account.AccountId),
        AccountName: this.display(account.AccountName),
        Status: this.display(account.Status),
        IsLsoAcknowledged: account.IsLsoAcknowledged === true ? 'Yes' : account.IsLsoAcknowledged === false ? 'No' : '-',
        CustomerAttachment: this.display(comments),
        Submitter: this.display(account.Submitter || contactSubmitters.join(', ')),
        QuestionsAndAnswers: Array.isArray(account.QuestionsAndAnswers) ? account.QuestionsAndAnswers : []
      };
    });
  }

  private display(value: any): string {
    return value === undefined || value === null || value === '' ? '-' : String(value);
  }

  public updatePaginatedData(): void {
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.pagedData = this.dataSource.slice(startIndex, endIndex);
  }


  get startIndex(): number {
    return this.currentPage * this.pageSize + 1;
  }

  get endIndex(): number {
    const end = (this.currentPage + 1) * this.pageSize;
    return Math.min(end, this.totalCount);
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  togglePageSizeMenu(): void {
    this.isPageSizeMenuOpen = !this.isPageSizeMenuOpen;
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 0;
    this.isPageSizeMenuOpen = false;
    this.updatePaginatedData();
    this.changeDetect.detectChanges();
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updatePaginatedData();
      this.changeDetect.detectChanges();
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.updatePaginatedData();
      this.changeDetect.detectChanges();
    }
  }

  goToFirstPage(): void {
    this.currentPage = 0;
    this.updatePaginatedData();
    this.changeDetect.detectChanges();
  }

  goToLastPage(): void {
    this.currentPage = this.totalPages - 1;
    this.updatePaginatedData();
    this.changeDetect.detectChanges();
  }
}
